import { useCallback, useEffect, useState } from 'react';
import { cancelMyAppointment, getMyOpenAppointment, MyAppointment } from '../../../service/telemedicine/api';
import { getErrorMessage } from '../utils/errors';

export type OpenAppointmentPhase = 'checking' | 'has-existing' | 'none';

const OPEN_APPOINTMENT_STORAGE_KEY = 'partiu:telemedicine:openAppointment';

function readStoredAppointment(): MyAppointment | null {
  try {
    const raw = window.localStorage.getItem(OPEN_APPOINTMENT_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as MyAppointment) : null;
  } catch {
    return null;
  }
}

export function storeAppointment(appointment: MyAppointment | null) {
  try {
    if (appointment) window.localStorage.setItem(OPEN_APPOINTMENT_STORAGE_KEY, JSON.stringify(appointment));
    else window.localStorage.removeItem(OPEN_APPOINTMENT_STORAGE_KEY);
  } catch {
    // localStorage indisponível (modo privado etc.) — segue só com o estado da API.
  }
}

export interface UseOpenAppointmentResult {
  phase: OpenAppointmentPhase;
  appointment: MyAppointment | null;
  isCancelling: boolean;
  cancelError: string;
  refresh: () => Promise<void>;
  cancel: () => Promise<void>;
}

/**
 * Consulta em aberto do usuário, verificada assim que a tela de Telemedicina abre.
 *
 * Enquanto existir uma consulta em aberto o usuário não pode marcar outra (o backend
 * também barra, devolvendo 409 em `POST /telemedicine/scheduling/book/`) — só depois de
 * concluí-la ou cancelá-la aqui. O cache local (localStorage) serve apenas para pintar a
 * tela sem esperar a rede; quem manda é sempre a resposta da API.
 */
export function useOpenAppointment(): UseOpenAppointmentResult {
  const [phase, setPhase] = useState<OpenAppointmentPhase>('checking');
  const [appointment, setAppointment] = useState<MyAppointment | null>(() => readStoredAppointment());
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');

  const refresh = useCallback(async () => {
    setPhase('checking');
    try {
      const response = await getMyOpenAppointment();
      setAppointment(response.data ?? null);
      storeAppointment(response.data ?? null);
      setPhase(response.data ? 'has-existing' : 'none');
    } catch {
      // Sem confirmação da API: se havia algo em cache local, mostra por segurança
      // (evita deixar marcar 2 consultas por falha momentânea de rede); senão, libera.
      setPhase(readStoredAppointment() ? 'has-existing' : 'none');
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const cancel = useCallback(async () => {
    if (!appointment) return;
    setIsCancelling(true);
    setCancelError('');
    try {
      await cancelMyAppointment(appointment.id);
      storeAppointment(null);
      setAppointment(null);
      setPhase('none');
    } catch (error) {
      setCancelError(getErrorMessage(error, 'Não foi possível cancelar o agendamento agora. Tente novamente.'));
    } finally {
      setIsCancelling(false);
    }
  }, [appointment]);

  return { phase, appointment, isCancelling, cancelError, refresh, cancel };
}
